import React, { lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// Import context provider for header titles
import { HeaderTitleProvider } from "./components/header/HeaderTitleContext";

// Import layout components
import PublicLayout from "./components/web/PublicLayout";
import Layout from "./default/MainLayout"; 

// Import public pages
import WebCourses from "./components/web/WebCourses";
import WebEvents from "./components/web/WebEvents";
import WebTestimonials from "./components/web/WebTestimonials";
import CustomPageRenderer from "./components/web/CustomPageRenderer";
import {
  WebGalleryPage,
  WebHolidaysPage,
} from "./components/web/WebUpdatePages";
import {
  WebsiteHomePage,
  WebsiteAboutPage,
  WebsiteAcademicsPage,
  WebsiteAdmissionsPage,
  WebsiteFacilitiesPage,
  WebsiteResultsPage,
  WebsiteAnnouncementsPage,
  WebsiteNewsPage,
  WebsiteCareersPage,
  WebsiteContactPage,
} from "./components/web/pages/WebsiteManagedPages";
import HomeWorks from "./components/homework/Homeworks";

// Import authentication pages
import LoginPage from "./components/login/LoginPage";
import SchoolSelectPage from "./components/login/SchoolSelectPage";
import ForgotPasswordPage from "./components/login/ForgotPasswordPage";
import ResetPasswordPage from "./components/login/ResetPasswordPage";
import UnauthorizedPage from "./components/UnauthorizedPage";
import RequireAuth from "./RequireAuth";

// Import protected pages (examples)
import Students from "./components/studentList/Students";
import StudentsAnalytics from "./components/studentList/StudentsAnalytics";
import CreateStudent from "./components/studentList/CreateStudent";
import EmployeeLeaveRequests from "./components/leaves/EmployeeLeaveRequests";
import ExamResults from "./components/exams/ExamResults";
import FeeTypeFormComponent from "./components/fees/FeeTypeFormComponent";
import FeeListComponent from "./components/fees/FeeListComponent";
import SectionManagement from "./components/sections/SectionManagement";
import TeacherSubjects from "./components/subject/TeacherSubjects";
import Subjects from "./components/subject/Subjects";
import TimetableComponent from "./components/timetable/TimetableComponent";
import ViewEmployeeDetails from "./components/employees/ViewEmployeeDetails";
import TeacherStatistics from "./components/employees/TeacherStatistics";
import ClassManagement from "./components/class/ClassManagement";
import HolidaysListAndForm from "./components/holidays/HolidaysListAndForm";
import FeeFormComponent from "./components/fees/FeeFormComponent";
import Account from "./components/Account";
import AssignTeacherComponent from "./components/subject/TeacherSubjects";
import SendNotification from "./components/send_notifications/SendMessage";
import Buses from "./components/transportation/Buses";
import MapTrack from "./components/transportation/MapTrack";
import AddRouteToBus from "./components/transportation/AddRouteToBus";
import Transportation from "./components/transportation/Transportation";
import BusStopManager from "./components/transportation/BusStopManager";
import RoutesPage from "./components/transportation/Routes";
import StudentDetailsHolder from "./components/studentList/studentDetails/StudentDetailsHolder";
import CreateEmployee from "./components/employees/CreateEmployee";
import AchievementEntryPage from "./components/achievements/AchievementEntryPage";
import AdminSettings from "./components/AdminSettings";
import RegularizationRequestList from "./components/attendance/RegularizationRequestList";
import LeaveCategoryManager from "./components/payroll/LeaveTypeManager";
import ManageFeesByClass from "./components/fees/ManageFeesByClass";
import LeaveBalanceManager from "./components/leaves/LeaveBalanceManager";
import AssignSubjectsTable from "./components/subject/AssignSubjectsTable";
import ImageUploadAndView from "./components/gallery/ImageUploadAndView";
import ResultsTable from "./components/exams/ExamResults";
import EventListAndForm from "./components/events/EventListAndForm";
import Employees from "./components/employees/Employees";
import AttendanceComponent from "./components/attendance/AttendanceComponent";
import Page404 from "./pages/page404";
import Page500 from "./pages/page500";

import UserProfileSection from "./components/settings/UserProfileSection";
import SchoolSettings from "./components/school/SchoolSettings";

import { ROLE_GROUPS } from "./config/accessControl";
import Dashboard from "./components/dashboard/Dashboard";

import PricingSection from "./components/landing/PricingSection";
import LandingPage from "./components/landing/LandingPage";
import RootRoute from "./components/RootRoute";
import PrivacyPolicy from "./components/web/PrivacyPolicy";
import TermsOfServiceComponent from "./components/web/TermsOfServiceComponent";
import DeleteAccount from "./components/web/DeleteAccount";
import StudentFeeManagement from "./components/studentList/studentDetails/fee_management/fee/StudentFeeManagement";

import FeeTermList from "./components/fees/termFee/FeeTermList";
import AccountPage from "./components/send_notifications/AccountPage";
import { isUserAuthenticated, persistAuthSession } from "./utils/authSession";
import { getDefaultAuthorizedPath } from "./utils/roleAccess";
import GlobalApiErrorSnackbar from "./components/common/GlobalApiErrorSnackbar";
import GlobalLoader from "./components/common/GlobalLoader";
import SeoRouteManager from "./components/common/SeoRouteManager";
import DocumentVerifyPage from "./components/documents/DocumentVerifyPage";
import TeacherExamMarksEntry from "./components/teacher/TeacherExamMarksEntry";
import AiLessonPlanner from "./components/teacher/AiLessonPlanner";
const SchoolsList = lazy(() => import("./components/schools/SchoolsList"));
const RegisterSchool = lazy(() => import("./components/schools/RegisterSchool"));
const ViewSchool = lazy(() => import("./components/schools/ViewSchool"));
const ExpenseDashboard = lazy(() => import("./components/expenses/ExpenseDashboard"));
const LibraryManagementComponent = lazy(() => import("./components/library/LibraryManagementComponent"));
const SalaryComponentManager = lazy(() => import("./components/payroll/SalaryComponentManager"));
const PayslipManager = lazy(() => import("./components/payroll/PayslipManager"));
const SalaryTemplateManager = lazy(() => import("./components/payroll/SalaryTemplateManager"));
const EmployeeSalaryDetails = lazy(() => import("./components/employees/EmployeeSalaryDetails"));
const DownloadReports = lazy(() => import("./components/DownloadReports"));
const PrintTimetable = lazy(() => import("./components/pdf/PrintTimetable"));
const PayrollDashboard = lazy(() => import("./components/payroll/PayrollDashboard"));
const FeeManagement = lazy(() => import("./components/fees/FeesManagement"));
const ExaminationMainCompont = lazy(() => import("./components/exams/ExaminationMainCompont"));
const HallTicketPage = lazy(() => import("./components/exams/HallTicketPage"));
const BannerList = lazy(() => import("./components/mobile/BannerList"));
const BannerForm = lazy(() => import("./components/mobile/BannerForm"));
const MobileWebTabHolder = lazy(() => import("./components/mobile/MobileWebTabHolder"));
const WebsiteManager = lazy(() => import("./components/mobile/WebsiteManager"));
const AiSetupWizard = lazy(() => import("./components/ai/AiSetupWizard"));
const CommandCenterPage = lazy(() => import("./components/dashboard/CommandCenterPage"));
const DocumentAutomationPage = lazy(() => import("./components/documents/DocumentAutomationPage"));
const DocumentTemplateGallery = lazy(() => import("./components/documents/DocumentTemplateGallery"));
const DocumentSettingsPage = lazy(() => import("./components/documents/DocumentSettingsPage"));
const ServerManagementPage = lazy(() => import("./components/admin/ServerManagementPage"));
const ScheduleTasksPage = lazy(() => import("./components/admin/ScheduleTasksPage"));
const SubscriptionWaiverManagementPage = lazy(() => import("./components/admin/SubscriptionWaiverManagementPage"));
const BillingConsolePage = lazy(() => import("./components/admin/BillingConsolePage"));
const HostelManagementPage = lazy(() => import("./components/hostel/HostelManagementPage"));
const SubscriptionPaymentsPage = lazy(() => import("./components/billing/SubscriptionPaymentsPage"));


const AppContent = () => {
  const handleLogin = (data) => {
    persistAuthSession(data);
  };

  const isAuthenticated = isUserAuthenticated();
  const storedSchoolLogo =
    localStorage.getItem("schoolLogo") ||
    "https://skhool-media-prod-193482297265.s3.ap-south-1.amazonaws.com/skhool/prod/product/public/images/skhool_logo.png";
  const storedSchoolName = localStorage.getItem("schoolName") || "Skhool.In";
  const defaultPublicRoute = "/";
  const loginRouteElement = <LoginPage handleLogin={handleLogin} />;


  return (
    <HeaderTitleProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <GlobalApiErrorSnackbar />
        <GlobalLoader />
        <SeoRouteManager />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfServiceComponent />} />
          <Route path="/delete-account" element={<DeleteAccount />} />

          {/* Public Routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route path="preview-home" element={<WebsiteHomePage />} />
            <Route path="home" element={<Navigate to={defaultPublicRoute} replace />} />
            <Route path="about" element={<WebsiteAboutPage />} />
            <Route path="courses" element={<WebCourses />} />
            <Route path="web-events" element={<WebEvents />} />
            <Route path="announcements" element={<WebsiteAnnouncementsPage />} />
            <Route path="news" element={<WebsiteNewsPage />} />
            <Route path="holidays" element={<WebHolidaysPage />} />
            <Route path="gallery" element={<WebGalleryPage />} />
            <Route path="testimonials" element={<WebTestimonials />} />
            <Route path="contact" element={<WebsiteContactPage />} />
            <Route path="facilities" element={<WebsiteFacilitiesPage />} />
            <Route path="admissions" element={<WebsiteAdmissionsPage />} />
            <Route path="academics" element={<WebsiteAcademicsPage />} />
            <Route path="result" element={<Navigate to="/academic-results" replace />} />
            <Route path="results" element={<Navigate to="/academic-results" replace />} />
            <Route path="careers" element={<WebsiteCareersPage />} />
            <Route path="page/home" element={<Navigate to="/" replace />} />
            <Route path="page/about" element={<Navigate to="/about" replace />} />
            <Route path="page/academics" element={<Navigate to="/academics" replace />} />
            <Route path="page/admissions" element={<Navigate to="/admissions" replace />} />
            <Route path="page/facilities" element={<Navigate to="/facilities" replace />} />
            <Route path="page/contact" element={<Navigate to="/contact" replace />} />
            <Route path="page/results" element={<Navigate to="/results" replace />} />
            <Route path="page/careers" element={<Navigate to="/careers" replace />} />
            <Route path="page/announcements" element={<Navigate to="/announcements" replace />} />
            <Route path="page/news" element={<Navigate to="/news" replace />} />
            <Route path="page/:slug" element={<CustomPageRenderer />} />
            <Route path="verify/document/:verificationCode" element={<DocumentVerifyPage />} />
          </Route>

        
        

          {/* Authentication Routes */}
          <Route path="/logi" element={<Navigate to="/login" replace />} />
          <Route path="/result" element={<Navigate to="/academic-results" replace />} />
          <Route path="/select-school" element={<SchoolSelectPage />} />
          <Route path="/login" element={loginRouteElement} />
          <Route path="/school-owner/register" element={<RegisterSchool publicSignup />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboad" element={<Navigate to="/dashboard" replace />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected Routes */}
          <Route element={<RequireAuth />}>
            <Route
              path="/"
              element={<Layout schoolLogo={storedSchoolLogo} schoolName={storedSchoolName} />}
            >


            {/* Dashboard */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherOnly} />}>
              <Route path="teacher-dashboard" element={<Navigate to="/dashboard" replace />} />
              <Route path="teacher/exam-marks" element={<TeacherExamMarksEntry />} />
              <Route path="teacher/lesson-plan" element={<AiLessonPlanner />} />
            </Route>
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="schedule-tasks" element={<ScheduleTasksPage />} />
            </Route>
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.studentOnly} />}>
              <Route path="student-dashboard" element={<Navigate to="/dashboard" replace />} />
            </Route>

            {/* mobile banners */}


            {/* Students */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="students" element={<Navigate to="/students/list" replace />} />
              <Route path="students/analytics" element={<StudentsAnalytics />} />
              <Route path="students/list" element={<Students />} />
              <Route path="reports" element={<DownloadReports />} />
              <Route path="student/add" element={<CreateStudent />} />
              <Route path="student/edit/:studentId" element={<CreateStudent editMode={true} />} />
            </Route>

            {/* Events (Admin Only) */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.dashboard} />}>
              <Route path="dashboard" element={<Dashboard />} />
            </Route>

            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.schoolManagement} />}>
              <Route path="subscription/payments" element={<SubscriptionPaymentsPage />} />
            </Route>

            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.schoolManagement} />}>
              <Route path="server-management" element={<ServerManagementPage />} />
              <Route path="admin/subscription-management" element={<SubscriptionWaiverManagementPage />} />
              <Route path="events" element={<EventListAndForm />} />
              <Route path="photo-gallery" element={<ImageUploadAndView />} />
              <Route path="hostel-management" element={<HostelManagementPage />} />
              <Route path="ai-setup" element={<AiSetupWizard />} />
              <Route path="smart-alerts" element={<CommandCenterPage />} />
              <Route path="student-risk" element={<CommandCenterPage />} />
              <Route path="document-hub" element={<CommandCenterPage />} />
              <Route path="mis-dashboard" element={<CommandCenterPage />} />
              <Route path="document-automation" element={<DocumentAutomationPage />} />
              <Route path="document-templates" element={<DocumentTemplateGallery />} />
              <Route path="document-settings" element={<DocumentSettingsPage />} />

            <Route path="mobile-web-applications" element={<MobileWebTabHolder />} />
            <Route path="web-editor" element={<WebsiteManager />} />

            <Route path="mobile">
             <Route index element={<BannerList />} />
             <Route path="banners/new" element={<BannerForm />} /> 
             <Route path="banners/edit/:bannerId" element={<BannerForm />} /> 
           </Route>
           

            
            </Route>

            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.superAdminOnly} />}>
              <Route path="admin/billing-console" element={<BillingConsolePage />} />
            </Route>

            {/* Teachers */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.employeeAdmin} />}>
              <Route path="employees" element={<Employees />} />
              <Route path="teacher/add" element={<CreateEmployee />} />
              <Route path="teacher/edit/:employeId" element={<CreateEmployee editMode={true} />} />
              <Route path="teacher/edit/:employeeId" element={<CreateEmployee editMode={true} />} />
              <Route path="achievements/add" element={<AchievementEntryPage />} />
              <Route path="employee/:employeId" element={<ViewEmployeeDetails />} />
              <Route path="employee/:employeeId" element={<ViewEmployeeDetails />} />
              <Route path="employee/:employeId/statistics" element={<TeacherStatistics />} />
              <Route path="employee/:employeeId/statistics" element={<TeacherStatistics />} />
            </Route>

            {/* Subjects */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="subjects" element={<Subjects />} />
              <Route path="subjects/assign" element={<TeacherSubjects />} />
              <Route path="subjects/teacher/assign" element={<AssignTeacherComponent />} />
            </Route>

            {/* Attendance */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="attendance" element={<AttendanceComponent />} />
              <Route path="regularization" element={<RegularizationRequestList />} />
            </Route>

            {/* Exams */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.exams} />}>
              <Route path="examination-types" element={<ExaminationMainCompont />} />
              <Route path="examination-types/new" element={<ExaminationMainCompont />} />
              <Route path="examination-types/:examinationTypeId" element={<ExaminationMainCompont />} />
              <Route path="hall-tickets" element={<HallTicketPage />} />
              <Route path="print/timetable/:examinationTypeId" element={<PrintTimetable />} />
            </Route>

            {/* Fees */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.finance} />}>
              <Route path="fee-management" element={<FeeManagement />} />
              <Route path="fees" element={<FeeListComponent />} />
              <Route path="fees/terms-fee" element={<FeeTermList />} />

              <Route path="expenses" element={<ExpenseDashboard />} />
              <Route path="fees/create" element={<FeeFormComponent />} />
              <Route path="fees/edit/:id" element={<FeeFormComponent />} />
              <Route path="fees/types" element={<FeeTypeFormComponent />} />
              <Route path="fees/byclass" element={<ManageFeesByClass />} />
              <Route path="fee-types/create" element={<FeeTypeFormComponent />} />
              <Route path="fee-types/edit/:id" element={<FeeTypeFormComponent />} />

              <Route path="student/:studentId/assign-fee" element={<StudentFeeManagement />} />
            </Route>

            {/* Salary & Payroll (Admin Only) */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.finance} />}>
              <Route path="payroll-dashboard" element={<PayrollDashboard />} />
              <Route path="payroll-components" element={<SalaryComponentManager />} />
              <Route path="payslips" element={<PayslipManager />} />
              <Route path="salary-template" element={<SalaryTemplateManager />} />
              <Route path="employee-salaries" element={<EmployeeSalaryDetails />} />
              <Route path="leave-types" element={<LeaveCategoryManager />} />
              <Route path="leave-balance" element={<LeaveBalanceManager />} />
              <Route path="class-subject-assign" element={<AssignSubjectsTable />} />
            </Route>

            {/* Classes */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.admin} />}>
              <Route path="classes" element={<ClassManagement />} />
            </Route>

            {/* Timetable */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="timetable" element={<TimetableComponent />} />
            </Route>

            {/* Homework */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="hworks" element={<HomeWorks/>} />
            </Route>

            {/* Holidays */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="hdays" element={<HolidaysListAndForm />} />
            </Route>

            {/* Admin Settings (Super Admin Only) */}
            <Route
              element={
                <UnauthorizedPage
                  allowedRoles={ROLE_GROUPS.superAdminOnly}
                />
              }
            >
              <Route path="admin" element={<Navigate to="/admin/settings" replace />} />
              <Route path="admin/settings" element={<AdminSettings />} />
            </Route>

            {/* Schools (Super Admin Only) */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.superAdminOnly} />}>
              <Route path="schools/list" element={<SchoolsList />} />
              <Route path="register-school" element={<RegisterSchool />} />
              <Route path="edit-school/:schoolId" element={<RegisterSchool />} />
              <Route path="view-school/:schoolId" element={<ViewSchool />} />
              <Route path="admin/message-balance" element={<AccountPage />} />
              
            </Route>

            {/* Sections */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.admin} />}>
              <Route path="school/:schoolId/sections" element={<SectionManagement />} />
            </Route>

            {/* Leave */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherSelfService} />}>
              <Route path="leave-management" element={<EmployeeLeaveRequests />} />
            </Route>

            {/* Library */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.library} />}>
              <Route path="library" element={<LibraryManagementComponent />} />
            </Route>

            {/* School Settings */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.admin} />}>
              <Route path="school-settings" element={<SchoolSettings />} />
            </Route>

            {/* Exam Results */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.results} />}>
              <Route path="dashboard/results" element={<Navigate to="/academic-results" replace />} />
              <Route path="academic-results" element={<ResultsTable />} />
              <Route path="academic-results/:examinationTypeId" element={<ResultsTable />} />
              <Route path="student/:studentId/results" element={<ExamResults />} />
            </Route>

            {/* Account */}
            <Route
              element={
                <UnauthorizedPage
                  allowedRoles={ROLE_GROUPS.account}
                />
              }
            >
              <Route path="account" element={<Account />} />
            </Route>

            {/* Send Notifications */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="send-notifications" element={<SendNotification />} />
            </Route>

            {/* Student Details */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.schoolManagement} />}>
              <Route path="school/:studentId/*" element={<UserProfileSection />} />
            </Route>
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.teacherEnabled} />}>
              <Route path="students/:studentId/*" element={<StudentDetailsHolder />} />
              <Route path="student/:studentId/*" element={<StudentDetailsHolder />} />
            </Route>

            {/* Transportation */}
            <Route element={<UnauthorizedPage allowedRoles={ROLE_GROUPS.schoolManagement} />}>
              <Route path="transportation/*" element={<Transportation />}>
                <Route path="buses" element={<Buses />} />
                <Route path="routes" element={<RoutesPage />} />
                <Route path="map" element={<MapTrack />} />
                <Route path="add-route" element={<AddRouteToBus />} />
                <Route path="bus-stops" element={<BusStopManager />} />
                <Route path="*" element={<Navigate to="buses" replace />} />
              </Route>
            </Route>

            {/* Protected fallback: redirect any unmatched route to dashboard */}
            <Route path="*" element={<Navigate to={getDefaultAuthorizedPath()} replace />} />
            </Route>
          </Route>

          <Route path="/404" element={<Page404 />} />
          <Route path="/500" element={<Page500 />} />

            {/* landing */}
            <Route path="pricing" element={<PricingSection />} />
            <Route path="hello" element={<LandingPage />} />

        

          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? getDefaultAuthorizedPath() : "/"} replace />
            }
          />

        </Routes>
      </Router>
    </HeaderTitleProvider>
  );
};

export default AppContent;
